import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faFilter, faExternalLinkAlt, faPlayCircle } from '@fortawesome/free-solid-svg-icons';

const Portfolio = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Sample portfolio data
  const portfolioItems = [
    {
      id: 1,
      title: 'Ethio Coffee Branding',
      category: 'branding',
      description: 'Complete brand identity for premium Ethiopian coffee exporter',
      image: '/images/portfolio/ethio-coffee.jpg',
      tags: ['Logo Design', 'Packaging', 'Brand Guidelines'],
      link: '#',
      featured: true
    },
    {
      id: 2,
      title: 'Addis Tech Summit Website',
      category: 'web-development',
      description: 'Corporate website with event management system',
      image: '/images/portfolio/addis-tech.jpg',
      tags: ['React', 'Node.js', 'MongoDB'],
      link: '#',
      featured: true
    },
    {
      id: 3,
      title: 'Shega Mobile App',
      category: 'app-development',
      description: 'Cross-platform mobile application for local business directory',
      image: '/images/portfolio/shega-app.jpg',
      tags: ['React Native', 'Firebase', 'UI/UX'],
      link: '#',
      featured: false
    },
    {
      id: 4,
      title: 'Habesha Fashion Campaign',
      category: 'digital-marketing',
      description: 'Social media marketing campaign for traditional clothing brand',
      image: '/images/portfolio/habesha-fashion.jpg',
      tags: ['Instagram', 'Facebook Ads', 'Content Creation'],
      link: '#',
      featured: true
    }
  ];

  const categories = [
    { id: 'all', name: 'All Projects' },
    { id: 'branding', name: 'Branding' },
    { id: 'web-development', name: 'Web Development' },
    { id: 'app-development', name: 'Mobile Apps' },
    { id: 'digital-marketing', name: 'Digital Marketing' },
    { id: 'video-production', name: 'Video Production' },
    { id: 'seo', name: 'SEO' }
  ];

  const filteredItems = portfolioItems.filter(item => {
    const matchesCategory = activeFilter === 'all' || item.category === activeFilter;
    const matchesSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-gray-50 pt-24">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#FD5F20] to-orange-600 text-white py-20">
        <div className="container mx-auto px-6 lg:px-16 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-6 font-urbanist">
            Our Portfolio
          </h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto">
            Discover how we've helped businesses transform their digital presence 
            with innovative solutions and creative strategies.
          </p>
        </div>
      </section>

      {/* Filters and Search */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-6 lg:px-16">
          <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
            {/* Search */}
            <div className="relative w-full md:w-64">
              <FontAwesomeIcon 
                icon={faSearch} 
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                placeholder="Search projects..."
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FD5F20] focus:border-transparent"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Category Filters */}
            <div className="flex flex-wrap gap-2 justify-center">
              {categories.map(category => (
                <button
                  key={category.id}
                  onClick={() => setActiveFilter(category.id)}
                  className={`px-4 py-2 rounded-full font-medium transition-all ${
                    activeFilter === category.id
                      ? 'bg-[#FD5F20] text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>

          {/* Portfolio Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredItems.map(item => (
              <div 
                key={item.id} 
                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 group"
              >
                {/* Image Container */}
                <div className="relative overflow-hidden">
                  <div className="w-full h-64 bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                    <span className="text-gray-500">Project Image</span>
                  </div>
                  
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-70 transition-all duration-300 flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex gap-4">
                      <button className="bg-white text-[#FD5F20] p-3 rounded-full hover:bg-[#FD5F20] hover:text-white transition-colors">
                        <FontAwesomeIcon icon={faExternalLinkAlt} />
                      </button>
                      {item.category === 'video-production' && (
                        <button className="bg-white text-[#FD5F20] p-3 rounded-full hover:bg-[#FD5F20] hover:text-white transition-colors">
                          <FontAwesomeIcon icon={faPlayCircle} />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Featured Badge */}
                  {item.featured && (
                    <div className="absolute top-4 left-4 bg-[#FD5F20] text-white px-3 py-1 rounded-full text-sm font-semibold">
                      Featured
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-6">
                  <span className="text-sm text-[#FD5F20] font-semibold uppercase tracking-wide">
                    {categories.find(cat => cat.id === item.category)?.name}
                  </span>
                  <h3 className="text-xl font-bold text-gray-800 mt-2 mb-3 font-urbanist">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {item.description}
                  </p>
                  
                  {/* Tags */}
                  <div className="flex flex-wrap gap-2">
                    {item.tags.map(tag => (
                      <span 
                        key={tag}
                        className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Empty State */}
          {filteredItems.length === 0 && (
            <div className="text-center py-12">
              <FontAwesomeIcon icon={faSearch} className="text-gray-400 text-6xl mb-4" />
              <h3 className="text-2xl font-bold text-gray-600 mb-2">No projects found</h3>
              <p className="text-gray-500">Try adjusting your search or filter criteria</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-[#FD5F20] to-orange-600 text-white">
        <div className="container mx-auto px-6 lg:px-16 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 font-urbanist">
            Ready to Start Your Project?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Let's create something amazing together. Get in touch with our team to discuss your project.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/atocreative/Project" 
              className="bg-white text-[#FD5F20] px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              Start a Project
            </a>
            <a 
              href="/service" 
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-[#FD5F20] transition-colors"
            >
              View Services
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

// Make sure this is the default export
export default Portfolio;